import * as React from 'react';
import { CardAside, CardEmpty } from '@chess/api-dashboard/react';
import { useElementSize, SkeletonPlot, TextSkeleton, type CardProps } from '@chess/api-insights/charts';
import { dateLabel, dayAxis, dayOffsets } from '@chess/api-insights/days';
import { ENDING_TYPE_LABEL, endingCaseView } from '@chess/api-ending-case';
import { returnTarget } from '@chess/api-navigation';
import type { GameOutcome, Phase } from '@chess/api-model';
import type { StageScatterPoint, StageScatterResult } from './StageScatterAggregator.ts';
import { verticalScale, type ScaleKind } from './scale.ts';

export type StageScatterCardProps = CardProps<
  StageScatterResult & { from: number; to: number },
  {
    stage: Phase;
    /** The vertical scale: fitted to the games' swings (default), linear, or a symmetric log that spreads offsets near zero. */
    scale?: ScaleKind;
    onOpen?: (reviewPath: string, id: string) => void;
  }
>;

/** "the ending", "the middlegame": how the card names its stage. */
const STAGE: Record<Phase, string> = { opening: 'the opening', middlegame: 'the middlegame', ending: 'the ending' };
/** "Opening swings", "Middlegame swings", "Ending swings". */
const SWINGS: Record<Phase, string> = { opening: 'Opening swings', middlegame: 'Middlegame swings', ending: 'Ending swings' };
/** The card's title for a stage, which the frame draws ("Ending swings"). */
export const stageScatterTitle = (stage: Phase) => SWINGS[stage];
const ASIDE = 'Last 30 days';
const RESULT: Record<GameOutcome, string> = { win: 'Won', draw: 'Drawn', loss: 'Lost' };
/** The legend's order. */
const OUTCOMES: GameOutcome[] = ['win', 'draw', 'loss'];

/** A short landscape plot, so the card is as tall as its row neighbours. */
const WIDTH = 320;
/** The plot's height in chart units at its natural shape; the card grows it to fill its row (2026-10-08). */
const BASE_HEIGHT = 128;
const PAD = { left: 38, right: 10, top: 8, bottom: 18 };
/** Games of one day sit this far apart (chart units), inside the day's column. */
const SPREAD = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

const pct = (v: number) => `${Math.round(v)} %`;
/** "−45", "+20", "0": an offset in percentage points, for the tooltip's brackets. */
export function offsetLabel(offset: number): string {
  const r = Math.round(offset);
  return `${r > 0 ? '+' : r < 0 ? '−' : ''}${Math.abs(r)}`;
}
/** "+50 %", "0", "−100 %" for a tick. */
const tickLabel = (t: number) => (t > 0 ? `+${t} %` : t < 0 ? `−${Math.abs(t)} %` : '0');

/** "vs bob · Oct 2 · Lost · Rook ending · Slipped · Win chance 92 % → 0 % (−92)": what a dot's tooltip and label say. */
function describe(stage: Phase, p: StageScatterPoint): string {
  const ended = stage !== 'ending' && p.finishedHere && `ended in ${STAGE[stage]}`;
  return [
    p.opponent && `vs ${p.opponent}`,
    dateLabel(p.at),
    RESULT[p.outcome],
    p.endingType && ENDING_TYPE_LABEL[p.endingType],
    p.endingCase && p.endingCase !== 'won' && p.endingCase !== 'drawn' && endingCaseView(p.endingCase).label,
    `Win chance ${pct(p.start)} → ${pct(p.end)} (${offsetLabel(p.offset)})`,
    ended,
  ]
    .filter(Boolean)
    .join(' · ');
}

/**
 * Each scanned game's stage as a dot: the day it was played across, and up
 * the offset from the stage's start: the end of the stage (the result, for
 * the ending) minus the reader's expected score when it started, −100…+100
 * percentage points. The solid zero line is no change, "↑ Gained" and
 * "↓ Lost" mark the two halves: below it the stage lost value (far below a big loss, just below a subtle
 * one), above it a gain (a save, a swindle). Dots in the result's colour
 * (won, drawn, lost), named in the legend and the tooltip. A dot opens its
 * review at the stage's first move. One card for every stage (`stage`), so
 * the Middlegame and Ending screens read alike. The body of the card: the
 * section draws its frame and title (`stageScatterTitle`, `registerCard`,
 * @chess/api-dashboard).
 */
export const StageScatterCard: React.FC<StageScatterCardProps> = (props) => {
  if (props.loading)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <div className="lines stage-scatter-plot plot-fill">
          <SkeletonPlot fill label={`Loading the win chance through each of ${STAGE[props.stage].replace('the ', 'your ')}s`} />
        </div>
        <ul className="legend-row stage-scatter-legend">
          {OUTCOMES.map((o) => (
            <li key={o}>
              <span className={`legend-swatch stage-scatter-swatch-${o}`} aria-hidden="true" />
              {RESULT[o]} <TextSkeleton sample="0" />
            </li>
          ))}
        </ul>
      </>
    );
  return <LoadedCard {...props} />;
};

/** The card with its games: mounted once they are loaded, so the plot's box is measured from its first render. */
const LoadedCard: React.FC<Exclude<StageScatterCardProps, { loading: true }>> = (props) => {
  const [active, setActive] = React.useState<number>();
  // The plot fills the card's height (the row's, set by its neighbours): the
  // chart's height in its own units follows the box's measured shape, so
  // text and dots keep their size while the plot grows.
  const [box, size] = useElementSize<HTMLDivElement>();
  const HEIGHT = size && size.width > 0 ? Math.max(BASE_HEIGHT, Math.round((WIDTH * size.height) / size.width)) : BASE_HEIGHT;
  const { stage, points, from, to, onOpen } = props;
  const scale = verticalScale(props.scale ?? 'fitted', props.points.map((p) => p.offset));
  const nonLinear = scale.kind !== 'linear';
  if (!points.length)
    return (
      <>
        <CardAside>{ASIDE}</CardAside>
        <CardEmpty>{`No ${STAGE[stage].replace('the ', '')}s in the last 30 days.`}</CardEmpty>
      </>
    );
  const axis = dayAxis(from, to, PAD.left, WIDTH - PAD.right);
  const dx = dayOffsets(points.map((p) => p.at), from, axis.column, SPREAD);
  // The scale maps −100…+100 to −1…+1; the plot puts +1 at the top.
  const y = (v: number) => PAD.top + ((1 - scale.unit(v)) / 2) * (HEIGHT - PAD.top - PAD.bottom);
  const dots = points.map((p, i) => ({ x: axis.centre(p.at) + dx[i], y: y(p.offset) }));
  const shown = active === undefined ? undefined : points[active];
  const counts = OUTCOMES.map((o) => ({ o, n: points.filter((p) => p.outcome === o).length }));
  const open = (p: StageScatterPoint) => (p.reviewPath && onOpen ? () => onOpen(p.reviewPath!, `stage-scatter-${stage}-${p.id}`) : undefined);
  const middle = from + Math.floor((axis.days - 1) / 2) * DAY_MS;
  return (
    <>
      <CardAside>{ASIDE}</CardAside>
      <div className="lines stage-scatter-plot plot-fill" ref={box}>
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={`What each game's ${STAGE[stage].replace('the ', '')} gained or lost against the position at its start${nonLinear ? ` (non-linear scale, ${scale.kind === 'fitted' ? "fitted to the games' swings" : 'compressed towards ±100 %'})` : ''}: ${points.map((p) => describe(stage, p)).join('; ')}`}>
          {scale.ticks.map((t) => (
            <g key={t}>
              {t !== 0 && <line x1={PAD.left} x2={WIDTH - PAD.right} y1={y(t)} y2={y(t)} stroke="var(--viz-grid)" strokeWidth={1} />}
              <text x={PAD.left - 4} y={y(t) + 3} textAnchor="end">
                {tickLabel(t)}
              </text>
            </g>
          ))}
          <line className="stage-scatter-zero" x1={PAD.left} x2={WIDTH - PAD.right} y1={y(0)} y2={y(0)} />
          {/* Which way is good; the ticks and the tooltip carry the numbers. */}
          <text className="stage-scatter-direction" x={PAD.left + 4} y={y(100) + 9} aria-hidden="true">
            ↑ Gained
          </text>
          <text className="stage-scatter-direction" x={PAD.left + 4} y={y(-100) - 3} aria-hidden="true">
            ↓ Lost
          </text>
          {nonLinear && (
            // The uneven ticks show it too; this says it in words.
            <text className="stage-scatter-direction" x={WIDTH - PAD.right - 2} y={y(-100) - 3} textAnchor="end" aria-hidden="true">
              non-linear scale
            </text>
          )}
          {[from, middle, to].map((at, k) => (
            <text key={k} x={axis.centre(at)} y={HEIGHT - 4} textAnchor={k === 0 ? 'start' : k === 2 ? 'end' : 'middle'}>
              {dateLabel(at)}
            </text>
          ))}
          {points.map((p, i) => {
            const go = open(p);
            const { x: cx, y: cy } = dots[i];
            return (
              <g
                key={p.id}
                {...returnTarget(`stage-scatter-${stage}-${p.id}`)}
                className={`stage-scatter-point${go ? ' stage-scatter-point-link' : ''}${active === i ? ' stage-scatter-point-active' : ''}`}
                role={go ? 'link' : undefined}
                tabIndex={go ? 0 : undefined}
                aria-label={go ? `Open the review at ${STAGE[stage]}: ${describe(stage, p)}` : describe(stage, p)}
                data-track={go ? 'overview.scatter.point' : undefined}
                data-track-value={go ? stage : undefined}
                onClick={go}
                onMouseEnter={() => setActive(i)}
                onMouseLeave={() => setActive(undefined)}
                onFocus={() => setActive(i)}
                onBlur={() => setActive(undefined)}
                onKeyDown={
                  go
                    ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          go();
                        }
                      }
                    : undefined
                }>
                <circle className="chart-hit" cx={cx} cy={cy} r={7} />
                <circle className={`stage-scatter-dot stage-scatter-dot-${p.outcome}`} cx={cx} cy={cy} r={3.5} />
              </g>
            );
          })}
        </svg>
        {shown && (
          <div className="stage-scatter-tip" role="tooltip" style={{ left: `${(dots[active!].x / WIDTH) * 100}%`, top: `${(dots[active!].y / HEIGHT) * 100}%` }}>
            {describe(stage, shown)}
          </div>
        )}
      </div>
      <ul className="legend-row stage-scatter-legend">
        {counts.map(({ o, n }) => (
          <li key={o}>
            <span className={`legend-swatch stage-scatter-swatch-${o}`} aria-hidden="true" />
            {RESULT[o]} {n}
          </li>
        ))}
      </ul>
    </>
  );
};
